import { Folder } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * T-0099 / E10 — Folder tree (sidebar) for the documents library.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `Screens.documents` (folder list block + `folderItem` helper at L356).
 *
 * The folders are derived from the document classifications endpoint (each
 * distinct classification is rendered as a virtual folder with a count). An
 * explicit "All documents" entry is the default selection. Selecting a
 * folder filters the document grid on the right pane.
 *
 * Why this is its own component:
 *   - The prototype's left rail mixes the classification list with the
 *     ingestion-pipeline status block; the library component (right pane)
 *     drives selection from here. Keeping the tree isolated lets the
 *     ingestion block sit alongside it without crossing concerns.
 *   - The "classifications come back as the closest available list" hack
 *     documented in `apps/web/src/lib/api/documents.ts` may evolve into a
 *     proper `GET /engagements/:id/folders` endpoint; routing folder
 *     selection through this component keeps the call sites stable.
 *
 * Tokens (no inline hex): paper-50 bg / ink tokens for borders + text /
 * accent amber for the active state. Matches the prototype's
 * `.nav-item` rule on the left rail (paper-50 bg + ink-100 border for
 * the active folder).
 */

export interface FolderTreeItem {
  /** Stable key — usually the classification name or "all". */
  id: string;
  label: string;
  count: number;
}

export interface FolderTreeProps {
  items: FolderTreeItem[];
  activeId: string;
  onSelect: (id: string) => void;
  className?: string;
}

export function FolderTree({ items, activeId, onSelect, className }: FolderTreeProps) {
  return (
    <nav aria-label="Document folders" className={cn('flex flex-col gap-1', className)}>
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex h-8 items-center gap-2 rounded-card px-2.5 text-body-sm text-ink-700 transition-colors',
              'hover:bg-paper-50 hover:text-ink-900',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
              active && 'border border-ink-100 bg-paper-50 font-semibold text-ink-900',
            )}
          >
            <Folder
              className={cn(
                'h-3.5 w-3.5 flex-none',
                active ? 'text-accent-amber-600' : 'text-ink-500',
              )}
              aria-hidden="true"
            />
            <span className="flex-1 truncate text-left">{item.label}</span>
            <span className="font-mono text-meta text-ink-500">{item.count}</span>
          </button>
        );
      })}
    </nav>
  );
}
